import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { VIEW_ALL_ROLES } from "../lib/states";
import LeadDrawer from "../components/LeadDrawer";
import { CalendarCheck, Phone, Building2, Home, Video, User } from "lucide-react";

const COL_ICON = {
  "Follow-ups": Phone,
  "Showroom Visits": Building2,
  "Home Visits": Home,
  "Video Calls": Video,
};
const COL_ACCENT = {
  "Follow-ups": "text-stone-900 bg-stone-100 border-stone-300",
  "Showroom Visits": "text-violet-600 bg-violet-50 border-violet-200",
  "Home Visits": "text-emerald-600 bg-emerald-50 border-emerald-200",
  "Video Calls": "text-amber-600 bg-amber-50 border-amber-200",
};

function ActionCard({ l, showAssignee, onClick }) {
  return (
    <div onClick={onClick} data-testid={`agenda-card-${l.id}`}
      className="bg-white border border-stone-200 rounded-lg p-3 hover:shadow-md hover:border-stone-300 transition-all cursor-pointer">
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium text-sm text-stone-900 truncate">{l.name}</p>
        {l.is_hot && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 shrink-0">HOT</span>}
      </div>
      <p className="text-xs text-stone-500 mt-0.5">+91 {l.phone}</p>
      {showAssignee && (
        <p className="text-xs text-stone-400 mt-1.5 flex items-center gap-1">
          <User className="h-3 w-3" /> {l.assignee_name || l.interaction_assignee_name || "Unassigned"}
        </p>
      )}
    </div>
  );
}

export default function TodaysActions() {
  const { user } = useAuth();
  const [day, setDay] = useState("today");
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const seeAll = VIEW_ALL_ROLES.includes(user?.role);

  const load = (d) =>
    api.get(`/actions/agenda?day=${d}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(day); }, [day]);

  const columns = data?.columns || [];

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-4 sm:px-6 py-2.5 sm:py-0 sm:h-16 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 border-b border-stone-200">
        <div className="flex items-center gap-2">
          <CalendarCheck className="h-5 w-5 text-stone-900" />
          <div>
            <h1 className="font-heading text-xl tracking-tight text-stone-900">Today's Actions</h1>
            <p className="text-xs text-stone-500" data-testid="agenda-subtitle">
              {data?.total ?? 0} due {day} · {seeAll ? "all staff" : "your tasks"}
            </p>
          </div>
        </div>
        <div className="inline-flex bg-stone-100 rounded-lg p-1 self-start sm:self-auto">
          {[["today", "Today"], ["tomorrow", "Tomorrow"]].map(([k, label]) => (
            <button key={k} onClick={() => setDay(k)} data-testid={`agenda-tab-${k}`}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors ${day === k ? "bg-white shadow-sm text-stone-900 font-medium" : "text-stone-500 hover:text-stone-700"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-x-auto overflow-y-hidden kanban-scroll px-4 sm:px-6 py-5">
        <div className="flex gap-4 h-full min-w-max">
          {columns.map((col) => {
            const Icon = COL_ICON[col.label] || CalendarCheck;
            return (
              <div key={col.label} className="w-[280px] shrink-0 flex flex-col h-full" data-testid={`agenda-col-${col.label}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`h-7 w-7 rounded-md border flex items-center justify-center ${COL_ACCENT[col.label]}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <h2 className="text-sm font-semibold tracking-tight text-stone-800">{col.label}</h2>
                  </div>
                  <span className="text-xs font-medium text-stone-400" data-testid={`agenda-count-${col.label}`}>{col.leads.length}</span>
                </div>
                <div className="flex-1 overflow-y-auto space-y-2.5 pr-1">
                  {col.leads.map((l) => (
                    <ActionCard key={l.id} l={l} showAssignee={seeAll} onClick={() => setActive(l)} />
                  ))}
                  {col.leads.length === 0 && (
                    <div className="text-xs text-stone-400 text-center py-8 border border-dashed border-stone-200 rounded-lg">Nothing due</div>
                  )}
                </div>
              </div>
            );
          })}
          {columns.length === 0 && (
            <p className="text-sm text-stone-400">Loading agenda…</p>
          )}
        </div>
      </div>

      {active && <LeadDrawer leadId={active.id} onClose={() => setActive(null)} onSaved={() => load(day)} />}
    </div>
  );
}
